import React, { useState } from "react";
import { Link2, Check, Linkedin, MessageCircle, Mail } from "lucide-react";
import { CANONICAL_ORIGIN } from "./Seo";
import { shareVisit } from "../lib/shareTrack";
import { publicApi } from "../lib/api";

const btn =
  "inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-navy px-2.5 py-1.5 rounded-full hover:bg-slate-100 transition-colors";

/**
 * Quiet share controls: copy the link, or hand it to LinkedIn, WhatsApp or
 * email. It shares the ordinary page URL — which now unfurls on its own — with
 * a small tag on the end so the admin can see what a share brings back. No
 * third-party social scripts, so nothing here slows the page or follows the
 * reader around.
 */
export const ShareRow = ({ path, title, label = "Share", className = "", onTrack }) => {
  const [copied, setCopied] = useState(false);

  // A path may already carry a query (a shared guide, for example), so the
  // tracking tag is appended rather than assumed to be first.
  const [cleanPath] = path.split("?");
  const link = (network) =>
    `${CANONICAL_ORIGIN}${path}${path.includes("?") ? "&" : "?"}utm_source=${network}&utm_medium=share`;

  const track = (network) => {
    publicApi.shareEvent({ type: "share", path: cleanPath, network, title, visit: shareVisit() });
    onTrack?.(network);
  };

  const copy = async () => {
    track("copy");
    const url = link("copy");
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const el = document.createElement("textarea");
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      el.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };

  const open = (network, href) => {
    track(network);
    window.open(href, network === "email" ? "_self" : "_blank", "noopener,noreferrer");
  };

  const t = encodeURIComponent(title || "");

  return (
    <div className={`flex flex-wrap items-center gap-1 ${className}`} data-testid="share-row">
      <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mr-1.5">{label}</span>
      <button type="button" onClick={copy} className={btn} data-testid="share-copy">
        {copied ? <Check size={13} className="text-green-600" /> : <Link2 size={13} />}
        {copied ? "Link copied" : "Copy link"}
      </button>
      <button
        type="button"
        onClick={() =>
          open("linkedin", `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link("linkedin"))}`)
        }
        className={btn}
        data-testid="share-linkedin"
      >
        <Linkedin size={13} /> LinkedIn
      </button>
      <button
        type="button"
        onClick={() => open("whatsapp", `https://wa.me/?text=${t}%20${encodeURIComponent(link("whatsapp"))}`)}
        className={btn}
        data-testid="share-whatsapp"
      >
        <MessageCircle size={13} /> WhatsApp
      </button>
      <button
        type="button"
        onClick={() => open("email", `mailto:?subject=${t}&body=${encodeURIComponent(link("email"))}`)}
        className={btn}
        data-testid="share-email"
      >
        <Mail size={13} /> Email
      </button>
    </div>
  );
};

export default ShareRow;
